import React, { useEffect, useRef } from 'react'
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'

type VerificationCode<Name extends string> = Record<Name, string>

type FieldName = 'verificationCode'
interface Props {
  name: FieldName | undefined
  value: string | undefined
  setValue: UseFormSetValue<VerificationCode<FieldName>>
  watch: UseFormWatch<VerificationCode<FieldName>>
  register: UseFormRegister<VerificationCode<FieldName>>
  length?: number
  error?: string
}

const toDigits = (raw: string | undefined | null, length: number) =>
  (raw ?? '').replace(/\D/g, '').slice(0, length)

const fieldStyle: React.CSSProperties = {
  position: 'relative',
  width: '90%',
  margin: '0 auto',
}

const inputStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1,
  width: '100%',
  height: '100%',
  padding: 0,
  border: 'none',
  outline: 'none',
  opacity: 1,
  background: 'transparent',
  textAlign: 'center',
  fontSize: '1.5rem',
  color: 'transparent',
  caretColor: 'transparent',
  WebkitTextFillColor: 'transparent',
}

const slotStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '4rem',
  fontSize: '1.5rem',
  borderRadius: '8px',
}

const OtpInput: React.FC<Props> = ({
  name = 'verificationCode',
  value = '',
  setValue,
  register,
  length = 6,
  watch,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const digits = toDigits(watch(name) ?? value, length)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = toDigits(e.target.value, length)
    if (next === digits) return
    setValue(name, next, { shouldValidate: true })
  }

  const caretToEnd = () => {
    const el = inputRef.current
    if (!el) return
    el.setSelectionRange(el.value.length, el.value.length)
  }

  return (
    <>
      <input type="hidden" {...register(name)} />
      <div style={fieldStyle}>
        <input
          className="otp-code-input"
          ref={inputRef}
          data-testid="otpInput"
          data-cy="otpInput"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={length}
          value={digits}
          onChange={handleChange}
          onFocus={caretToEnd}
          onClick={caretToEnd}
          aria-label="Verification code"
          style={inputStyle}
        />
        <div
          aria-hidden="true"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${length}, 1fr)`,
            gap: '10px',
          }}
        >
          {Array.from({ length }).map((_, i) => (
            <div
              key={i}
              data-testid={`otpInput-${i}`}
              data-cy={`otpInput-${i}`}
              style={{
                ...slotStyle,
                border: `1px solid ${
                  i === digits.length && digits.length < length
                    ? '#333333'
                    : '#cccccc'
                }`,
              }}
            >
              {digits[i] ?? ''}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default OtpInput
